/**
 * Service Worker Registration and Update Manager
 *
 * Ovaj modul upravlja Service Worker-om i automatskim osvežavanjem aplikacije
 * kada se detektuje nova verzija.
 */

// Konfiguracija
const CONFIG = {
    // Koliko često proveravati novu verziju (u milisekundama)
    CHECK_INTERVAL: 5 * 60 * 1000, // 5 minuta
    // Da li automatski osvežiti bez pitanja korisnika
    AUTO_REFRESH: false,
    // Da li prikazati notifikaciju o novoj verziji
    SHOW_NOTIFICATION: true
};

// Callback funkcije koje će biti pozvane kada se detektuje nova verzija
let onUpdateFoundCallback = null;
let onUpdateReadyCallback = null;

/**
 * Registruje Service Worker
 */
export function register(config = {}) {
    if ('serviceWorker' in navigator) {
        // Public URL iz environment varijable
        const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);

        // Ne registruj SW ako je origin različit
        if (publicUrl.origin !== window.location.origin) {
            return;
        }

        window.addEventListener('load', () => {
            const swUrl = `${process.env.PUBLIC_URL || ''}/service-worker.js`;
            registerValidSW(swUrl, config);
        });
    }
}

/**
 * Registruje validan Service Worker
 */
function registerValidSW(swUrl, config) {
    navigator.serviceWorker
        .register(swUrl)
        .then((registration) => {
            // Proveri update odmah
            registration.update();

            // Periodično proveravaj update
            setInterval(() => {
                registration.update();
            }, CONFIG.CHECK_INTERVAL);

            registration.onupdatefound = () => {
                const installingWorker = registration.installing;
                if (installingWorker == null) {
                    return;
                }

                installingWorker.onstatechange = () => {
                    if (installingWorker.state === 'installed') {
                        if (navigator.serviceWorker.controller) {
                            // Nova verzija je dostupna
                            // console.log('[SW] Nova verzija dostupna!');

                            if (config.onUpdate) {
                                config.onUpdate(registration);
                            }

                            if (onUpdateReadyCallback) {
                                onUpdateReadyCallback(registration);
                            }
                        } else {
                            // Sadržaj je keširan za offline upotrebu
                            // console.log('[SW] Sadržaj keširan za offline.');

                            if (config.onSuccess) {
                                config.onSuccess(registration);
                            }
                        }
                    }
                };
            };
        })
        .catch((error) => {
            console.error('[SW] Greška pri registraciji:', error);
        });
}

/**
 * Deregistruje Service Worker
 */
export function unregister() {
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready
            .then((registration) => {
                registration.unregister();
            })
            .catch((error) => {
                console.error(error.message);
            });
    }
}

/**
 * Postavlja callback za kada je update spreman
 */
export function onUpdateReady(callback) {
    onUpdateReadyCallback = callback;
}

/**
 * Forsira refresh aplikacije
 */
export function forceRefresh() {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        // Pošalji poruku SW da preskoči čekanje (bez očekivanja odgovora)
        try {
            navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
        } catch (e) {
            console.warn('[SW] Greška pri slanju SKIP_WAITING:', e);
        }
    }

    // Očisti sve keširane podatke
    if ('caches' in window) {
        caches
            .keys()
            .then((names) => {
                names.forEach((name) => {
                    caches.delete(name);
                });
            })
            .catch((e) => {
                console.warn('[Cache] Greška pri brisanju keša:', e);
            });
    }

    // Reload stranice sa kratkim odlaganjem da se poruke procesiraju
    setTimeout(() => {
        window.location.reload(true);
    }, 100);
}

/**
 * Proverava verziju sa servera
 */
export async function checkVersion() {
    try {
        // Dodaj timestamp da izbegneš keš
        const response = await fetch(`${process.env.PUBLIC_URL || ''}/version.json?t=${Date.now()}`, {
            cache: 'no-store'
        });

        if (!response.ok) {
            throw new Error('Nije moguće učitati version.json');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('[Version Check] Greška:', error);
        return null;
    }
}

/**
 * Poredi verzije i vraća true ako je nova verzija dostupna
 */
export function isNewerVersion(currentVersion, serverVersion) {
    if (!currentVersion || !serverVersion) return false;

    const current = currentVersion.split('.').map(Number);
    const server = serverVersion.split('.').map(Number);

    for (let i = 0; i < Math.max(current.length, server.length); i++) {
        const c = current[i] || 0;
        const s = server[i] || 0;

        if (s > c) return true;
        if (s < c) return false;
    }

    return false;
}

export default { register, unregister, forceRefresh, checkVersion, isNewerVersion, onUpdateReady };

